import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router';

import { FormContainer } from '@eika-kortbetjening/form-container';
import { EMPTY_PIN_ENTRY_FIELD, NewPinEntry, PinEntryFields } from '@eika-kortbetjening/pin-code';
import {
    isPinEntryFieldsFourDigits,
    isValidPin,
    isValidPinEntryLength,
} from '@eika-kortbetjening/pin-code/lib/utils/pin-utils';

import SkeletonList from '@/components/skeleton/SkeletonList';
import piwik from '@/piwik/piwik';
import { useSetNewPin } from '@/react-query/hooks/mutation/use-set-new-pin';
import { useCard } from '@/react-query/hooks/query/use-card';
import { navigateToCardDetails } from '@/utils/url/get-card-details-url-utils';
import { getRootUrl, navigateToHardBlockConfirm, navigateToPinChangeDecision } from '@/utils/url/get-url-utils';

import { NewPinEntryStatusMessage } from '@components/shared/NewPinEntryAttentionMessage';
import { useLinkProducer, useNavigate } from '@domain/app';
import useReasonQuery from '@hooks/query/use-reason-query';
import { useIsHaugesund } from '@hooks/use-bank-type';
import useText from '@hooks/use-text';
import { canSetPin } from '@utils/allowed-operations/card-allowed-operations-utils';

import Footer from './Footer';

import './NewPinEntry.scss';

const NewPinEntryContainer = () => {
    const [newPinForm, setNewPinForm] = useState<PinEntryFields>([
        EMPTY_PIN_ENTRY_FIELD,
        EMPTY_PIN_ENTRY_FIELD,
        EMPTY_PIN_ENTRY_FIELD,
        EMPTY_PIN_ENTRY_FIELD,
    ]);
    const [newPinInvalidError, setNewPinInvalidError] = useState<string | undefined>(undefined);

    const { data: card, isLoading } = useCard();
    const {
        formCancel,
        formNext,
        pinNeedsFourCharsError,
        pinInvalidError,
        chooseNewPinTitle,
        confirmChangePin,
        formBack,
    } = useText();
    const reasonQuery = useReasonQuery();
    const setPinQuery = useSetNewPin();
    const { mutate: setNewPin, isPending: isSettingPin, isSuccess: hasSetPin, isError: failedSetPin } = setPinQuery;
    const blockPinChange = useIsHaugesund();
    const location = useLocation();
    const navigate = useNavigate();
    const linkProducer = useLinkProducer();

    useEffect(() => {
        setNewPinInvalidError(undefined);
    }, [newPinForm]);

    if (typeof card === 'undefined' || isLoading) {
        return <SkeletonList />;
    }

    if (reasonQuery === null) {
        return <Navigate to={getRootUrl(linkProducer)} replace />;
    }

    if (canSetPin(card) && blockPinChange) {
        navigateToPinChangeDecision(navigate, location);

        return null;
    }

    const submitNewPinEntry = () => {
        if (!isValidPinEntryLength(newPinForm)) {
            setNewPinInvalidError(pinNeedsFourCharsError);
            return;
        }

        if (!isPinEntryFieldsFourDigits(newPinForm)) {
            setNewPinInvalidError(pinInvalidError);
            return;
        }

        if (!isValidPin(newPinForm)) {
            setNewPinInvalidError(pinInvalidError);
            return;
        }

        setNewPin({ cardType: card.type, cardId: card.id, newPin: newPinForm });
    };

    const next = () => {
        navigateToHardBlockConfirm(navigate)({
            card,
            selectedReason: reasonQuery,
            reorder: true,
            changedPin: true,
        });
    };

    const cancel = () => {
        navigateToCardDetails(navigate)(card);
    };

    const back = () => {
        navigateToPinChangeDecision(navigate, location);
        return null;
    };
    const isError = typeof newPinInvalidError !== 'undefined';

    return (
        <FormContainer
            className="replace-card-new-pin-entry-form"
            data-track-id={piwik.replaceCard.newPinForm.dataTrackId}
            data-track-linkname={piwik.replaceCard.newPinForm.dataTrackLinkname}
            id={piwik.replaceCard.newPinForm.dataTrackId}
        >
            <FormContainer.Header title={chooseNewPinTitle} />
            <FormContainer.Content>
                <div className="replace-card-new-pin-entry-form__form">
                    <div className="replace-card-new-pin-entry-form__form__new-pin-entry">
                        <NewPinEntry newPin={newPinForm} setNewPin={setNewPinForm} hasError={isError} piwik={piwik} />
                    </div>
                    <NewPinEntryStatusMessage newPinInvalidError={newPinInvalidError} setPinQuery={setPinQuery} />
                </div>
            </FormContainer.Content>
            <FormContainer.Footer>
                <Footer
                    isSettingPin={isSettingPin}
                    hasSetPin={hasSetPin}
                    isError={isError}
                    failedSetPin={failedSetPin}
                    formCancel={formCancel}
                    formNext={formNext}
                    formBack={formBack}
                    confirmChangePin={confirmChangePin}
                    cancel={cancel}
                    next={next}
                    back={back}
                    submitNewPinEntry={submitNewPinEntry}
                />
            </FormContainer.Footer>
        </FormContainer>
    );
};

export default NewPinEntryContainer;
